/**
 * 对话页「生图」入口的配置解析与执行（T23）。
 *
 * 职责边界：
 * - 只做「把当前会话的提供商（`ProviderConfig`）翻译成 `ResolvedImageConfig`」与
 *   「调 `createImageAdapter().generate()`」这两件事，生图协议本身仍在 `src/core/image`；
 * - 密钥沿用 T15 的 `secretStore`（`apiKeyRef` → 明文只在运行期解析，不写进配置）；
 * - 生图模型直接复用提供商配置里的 `defaultModel`（见交接单的取舍说明）。
 *
 * 本文件不修改 `src/core/**` 的任何实现。
 */

import type { GeneratedImage, ResolvedImageConfig } from '../../core/image';
import type { Attachment, ProviderConfig } from '../../core/types';
import { SERVER_PROVIDER_ID, providerPurpose } from '../../core/providers';
import { PROVIDERS_PATH } from '../providers/paths';
import { readSecret } from '../providers/secretStore';
import type { OfficialImageOption } from './officialModelCatalog';
import { modelGateway } from '../modelGateway';

/** 生图模型缺失时的提示（引导用户去配置页补默认模型）。 */
export const IMAGE_MODEL_MISSING_HINT = `当前提供商还没有默认模型，无法生图；请到「设置 → 提供商配置」（#${PROVIDERS_PATH}）补一个生图模型（如 dall-e-3 / gpt-image-1）。`;

/** 提供商不支持生图时的提示（Anthropic / Gemini 没有 OpenAI 兼容的 images 接口）。 */
export const IMAGE_UNSUPPORTED_HINT = `当前提供商不提供 OpenAI 兼容的生图接口；请到「设置 → 提供商配置」（#${PROVIDERS_PATH}）新增一个 OpenAI 兼容提供商，并把它设为当前会话使用的提供商。`;

/** 一次生图请求：提示词 + 取消信号 + 已解析的生图配置。 */
export interface ImageGeneratorRequest {
  prompt: string;
  signal: AbortSignal;
  config: ResolvedImageConfig;
}

/** 生图执行入口（可注入，测试用 fake 保证不联网）。 */
export type ImageGenerator = (request: ImageGeneratorRequest) => Promise<GeneratedImage[]>;

export interface ResolvedImageEntry {
  /** 可用的生图配置；为 null 时用 `hint` 引导用户配置。 */
  config: ResolvedImageConfig | null;
  /** 官方生图模型 slug；非 null 时走站内鉴权与计费接口。 */
  serverModel: string | null;
  /** 弹层里展示的配置说明 / 引导文案。 */
  hint: string;
}

/** 支持 OpenAI 兼容生图接口的提供商类型。 */
export function providerSupportsImage(provider: ProviderConfig): boolean {
  return (
    (provider.kind === 'openai' || provider.kind === 'custom') &&
    providerPurpose(provider) === 'image'
  );
}

/**
 * 由当前会话的提供商解析生图配置。
 *
 * 复用关系：`baseUrl` / `apiKeyRef` / `defaultModel` 都取自提供商配置，
 * 只有 `kind` 固定为 `openai-compatible`（T11a 目前只实现该协议）。
 * 提供商类型不支持、或缺少默认模型时返回 `config: null` + 明确的中文引导。
 */
export function resolveImageEntry(
  provider: ProviderConfig | null,
  official: OfficialImageOption | null = null,
  selectedModel = '',
): ResolvedImageEntry {
  if (provider !== null && providerSupportsImage(provider)) {
    const model = selectedModel.trim() !== '' ? selectedModel.trim() : (provider.defaultModel ?? '').trim();
    if (model !== '') {
      const apiKey = readSecret(provider.apiKeyRef);
      const config: ResolvedImageConfig = {
        id: `${provider.id}#image`,
        kind: 'openai-compatible',
        name: `${provider.name}（生图）`,
        baseUrl: provider.baseUrl,
        apiKeyRef: provider.apiKeyRef,
        defaultModel: model,
        enabled: true,
        ...(apiKey === null ? {} : { apiKey }),
      };
      return {
        config,
        serverModel: null,
        hint: `使用「${provider.name}」的 ${model} 生图；生成结果会自动存入图库。`,
      };
    }
  }
  if (official !== null) {
    return {
      config: null,
      serverModel: official.slug,
      hint: `使用官方生图模型「${official.displayName}」；生成结果会自动存入图库。`,
    };
  }
  if (provider === null) {
    return {
      config: null,
      serverModel: null,
      hint: '尚未配置可用的生图模型，请点击右侧 ＋ 添加自定义提供商，或让管理员发布官方生图模型。',
    };
  }
  if (!providerSupportsImage(provider)) {
    return { config: null, serverModel: null, hint: `${provider.name}：${IMAGE_UNSUPPORTED_HINT}` };
  }
  return {
    config: null,
    serverModel: null,
    hint: `${provider.name}：请选择或输入生图模型；也可以在「管理提供商」里设置默认模型。`,
  };
}

export interface ImageProviderChoice {
  id: string;
  label: string;
}

/** 生图提供商选择器：官方生图在前，其余为可用的 OpenAI 兼容自定义提供商。 */
export function imageProviderChoices(
  providers: readonly ProviderConfig[],
  officialOptions: readonly OfficialImageOption[],
): ImageProviderChoice[] {
  const choices: ImageProviderChoice[] = [];
  if (officialOptions.length > 0) {
    choices.push({ id: SERVER_PROVIDER_ID, label: '官方生图模型' });
  }
  for (const provider of providers) {
    if (provider.enabled && providerSupportsImage(provider)) {
      choices.push({ id: provider.id, label: provider.name });
    }
  }
  return choices;
}

export interface ImageModelChoice {
  value: string;
  label: string;
}

/** 生图模型候选：官方 slug，或提供商默认模型 + 模型列表。 */
export function imageModelChoices(
  providerId: string,
  provider: ProviderConfig | null,
  officialOptions: readonly OfficialImageOption[],
  catalogModels: readonly string[],
  currentModel = '',
): ImageModelChoice[] {
  if (providerId === SERVER_PROVIDER_ID) {
    return officialOptions.map((option) => ({ value: option.slug, label: option.label }));
  }
  if (provider === null) return [];
  const values = [
    provider.defaultModel ?? '',
    ...catalogModels,
    currentModel,
  ]
    .map((value) => value.trim())
    .filter((value) => value !== '');
  return [...new Set(values)].map((value) => ({ value, label: value }));
}

export interface ResolveSelectedImageEntryInput {
  providerId: string;
  model: string;
  providers: readonly ProviderConfig[];
  officialOptions: readonly OfficialImageOption[];
}

/**
 * 按用户在生图弹层里选择的提供商 / 模型解析本次生图配置。
 * 与当前对话绑定的提供商完全解耦。
 */
export function resolveSelectedImageEntry({
  providerId,
  model,
  providers,
  officialOptions,
}: ResolveSelectedImageEntryInput): ResolvedImageEntry {
  if (providerId === SERVER_PROVIDER_ID) {
    if (model.trim() === '') {
      return {
        config: null,
        serverModel: null,
        hint: '请选择官方生图模型。',
      };
    }
    const official = officialOptions.find((option) => option.slug === model.trim()) ?? null;
    if (official === null) {
      return {
        config: null,
        serverModel: null,
        hint: `官方生图模型「${model.trim()}」未发布或已下架；请重新选择。`,
      };
    }
    return resolveImageEntry(null, official ?? officialOptions[0] ?? null);
  }
  const provider =
    providers.find(
      (item) => item.id === providerId && item.enabled && providerSupportsImage(item),
    ) ?? null;
  return resolveImageEntry(provider, null, model);
}

/** 默认生图执行：经 T11a 的适配器注册表实例化后调用 `generate({ prompt })`。 */
export const defaultImageGenerator: ImageGenerator = ({ prompt, signal, config }) =>
  modelGateway.generateImage({ prompt, signal, config });

/** dataUrl → MIME（生成图片只有 dataUrl，没有单独的 mime 字段）。 */
function mimeFromDataUrl(dataUrl: string): string {
  const matched = /^data:([^;,]+)[;,]/.exec(dataUrl);
  return matched?.[1] ?? 'image/png';
}

/**
 * 把 `ChatMessage.images`（生成图片的 dataUrl 列表）转成可渲染的附件对象，
 * 与用户上传的 `ChatMessage.attachments` 走同一条渲染链路。
 */
export function generatedImageAttachments(
  messageId: string,
  images: readonly string[],
): Attachment[] {
  return images.map((dataUrl, index) => ({
    id: `${messageId}-image-${String(index)}`,
    kind: 'image',
    name: `生成图片 ${String(index + 1)}`,
    mime: mimeFromDataUrl(dataUrl),
    size: 0,
    dataUrl,
  }));
}
